import { IDisposable } from "../../../../Global/Base/IDisposable";
import { ActionContext, ActionType, IAction } from "../../../UI.Controls/Actions/IAction";
import { IFrame } from "../XGControls/IFrame";

/**
     * Reprezentuje uživatelské rozhraní celého formuláře, které umí vytvářet menu pro formulář a pro jednotlivé framy
     * Je možné vytvářet vlastní implementace
     * @includeToDoc
     */
export interface IFormUI extends IDisposable {
    /**
     * Obsahuje odkaz na menu spolecne pro celou skupinu formularu
     */
    formGroupMenu: IFormGroupMenu;
    /**
     * Vrací odkaz na menu formuláře
     */
    readonly formMenu: IFormMenu;
    /**
     * Vytvoří formulářové menu s ovládacími prvky týkajícími se formuláře jako celku
     */
    createFormMenu(): IFormMenu;

    /**
     * Vytvoří framové menu s ovládacími prvky týkajícími se jednoho konkrétního framu
     * @param sourceInfo Frame, ke kterému je menu vytvářeno nebo lokalizační klíč určující název framu
     */
    createFrameMenu(sourceInfo: IFrame): IMenu;

    /**
     * Umožňuje nastavit vlastní specifické property, na které mohou různé implementace reagovat
     * @param propertyName Název property - buď enum FormUICustomProperty nebo vlastní libovolný string
     * @param newValue Nastavovaná hodnota
     */
    setProperty(propertyName: FormUICustomProperty | string, newValue: any): void;

    /**
     * Jednoslovný název/kódové označení pro formUI.
     */
    formUIName: string;
}

export class IFormUI {
    private static _properties = ["createFormMenu", "createFrameMenu", "setProperty", "formGroupMenu", "formMenu"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !IFormUI._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Reprezentuje uživatelské rozhraní umožňující ovládat obecně formulář
 * Je možné vytvářet vlastní implementace
 * @includeToDoc
 */
export interface IFormMenu extends IDisposable {
    /** Pokud je nastaveno na true, skryje formulářové menu */
    hideFormMenu?: boolean;
    /**
     * Přidání akce do toolbaru
     * @param action Objekt reprezentující akci
     */
    addAction<T extends IAction>(action: T): T;

    /**
     * Umožňuje nastavit vlastní specifické property, na které mohou různé implementace reagovat
     * @param propertyName Název property - buď enum FormUICustomProperty nebo vlastní libovolný string
     * @param newValue Nastavovaná hodnota
     */
    setProperty(propertyName: FormUICustomProperty | string, newValue: any): void;
}

/**
 * Reprezentuje uživatelské rozhraní obsaujíci společné prvky pro všechny formuláře
 * Je možné vytvářet vlastní implementace
 * @includeToDoc
 */
export interface IFormGroupMenu extends IDisposable {
    /** Pokud je nastaveno na true, skryje menu stránky */
    hidePageMenu?: boolean;

    /**
     * Umožňuje nastavit vlastní specifické property, na které mohou různé implementace reagovat
     * @param propertyName Název property - buď enum FormUICustomProperty nebo vlastní libovolný string
     * @param newValue Nastavovaná hodnota
     */
    setProperty(propertyName: FormUICustomProperty | string, newValue: any): void;
}

export class IFormGroupMenu {
    private static _properties: string[] = [];
    private static _methods: (keyof(IFormGroupMenu))[] = ["setProperty"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Reprezentuje uživatelské rozhraní umožňující ovládat jeden frame
 * Je možné vytvářet vlastní implementace
 * @includeToDoc
 */
export interface IMenu extends IDisposable {

    /**
     * Přidání akce do toolbaru
     * @param icon Ikonka, která se zobrazuje na tlačítku akce
     * @param menuType Do jakého menu se má akce zařadit
     * @param action Objekt reprezentující akci
     */
    addAction<T extends IAction>(action: T): T;

    /**
     * Odstraní zaregistrovanou akci z menu
     * @param action Akce k odstranění
     */
    removeAction(action: IAction): void;

    /**
     * Vrací akci podle jejího typu, pokud jich je víc, tak vrátí první
     * @param actionType Typ akce
     */
    getAction<T extends IAction>(actionType: ActionType): T;

    /**
     * Vrací akce podle typu
     * @param actionType Typ akce
     */
    getActions<T extends IAction>(actionType?: ActionType): Iterable<T>;

    /**
     * Vrací akce podle použitého kontextu
     * @param actionContext Kontext akce
     */
    getActionsByContext<T extends IAction>(actionContext?: ActionContext): Iterable<T>;

    /**
     * Vytvoří element s ovládacími prvky, které se použijí v kontextu datového záznamu
     * @param dataKeyFetcher Funkce vracející aktuální identifikátor datového záznamu, pro který se generují ovládací prvky
     */
    renderRecordControls(dataKeyFetcher: () => string): HTMLElement;

    /**
     * Nastavuje viditelnost celého menu
     * @param visibility True pokud má být viditelný
     */
    setVisibility(visibility: boolean): void;

    /**
     * Umožňuje nastavit vlastní specifické property, na které mohou různé implementace reagovat
     * @param propertyName Název property - buď enum FormUICustomProperty nebo vlastní libovolný string
     * @param newValue Nastavovaná hodnota
     */
    setProperty(propertyName: FormUICustomProperty | string, newValue: any): void;

    /**
     * Vrací true, pokud UI podporuje pouze samostatný toolbar (např. Ribbon) nebo každý frame má vlastní toolbar.
     */
    usesSingleToolbar: boolean;

    showMenuLoader?(): void;
    hideMenuLoader?(): void;
}

export enum Icon {
    none,
    delete,
    new,
    save,
    copy,
    refresh,
    settings,
    settingsEqualizer,
    settingsEqualizerB,
    back,
    prev,
    next,
    multi,
    edit,
    more,
    export,
    print,
    search,
    layoutChange,
    treeGridSwitch,
    defaultIcon,
    detailFormActionIcon,
    openDetail,
    cancelChanges,
    favorite,
    selectFromLov,
    execute,
    executeAndClose,
    selectAll,
    selectNone,
    info,
    arrowUp,
    arrowDown,
    userLockUnlocked,
    prevRecord,
    nextRecord,
    recordActions,
    moveLeft,
    moveRight,
    filter,
    filterFilled,
    triangleBottom,
    graph,
    openOnMainForm = "ui-out-link",
    glasses = "doc-glasses",
    recordInfo = "doc-wboard-search",
    wfHistory = "ui-undo",
    wfParticipants = "user-group",
    wfStatus = "ui-pixel",
    webTabs = "web-tabs",
}

/**
 * Typ menu - kontextové menu nebo toolbar, do kterého se má prvek vložit.
 */
export enum MenuTypes {
    toolBar = 1,
    contextMenu = 2,
}

/**
 * Definuje různé specifické stavy, které je možné nastavit FormUI nebo některému menu
 */
export enum FormUICustomProperty {
    pageColor = 1,
    selected = 2,
    selectedPageColor = 3,
    notificationDot = 4,
    breadcrumbsElement = 5,
    formMenu = 6,
    formMenuVisibility = 7,
}
